import { Link } from "@inertiajs/react"
import type { ReactNode } from "react"

import { cx } from "@/components/ui/cx"

export interface Crumb {
  label: string
  href?: string
}

/*
 * The head of every console page — the surface spec's worklist and record
 * anatomy, composed once:
 *
 *   crumb line (mono, lowercase, `/` separated)        right-aligned stamp
 *   [mono code · hairline] title in the announcement voice        actions
 *   a fact line in notation, or a prose line in the working voice
 *
 * `facts` is for things that are measured ("62 jobs · Schools"); `description`
 * is for a sentence. A page passes one or the other, rarely both.
 */
export function PageHeader({
  crumbs,
  stamp,
  code,
  title,
  facts,
  description,
  actions,
  size = "md",
  children,
  className,
}: {
  crumbs?: Crumb[]
  /** A mono stamp on the right of the crumb line: "mirror synced 9 Sep 06:10". */
  stamp?: ReactNode
  /** A mono id before the title: "JOB-13966". */
  code?: ReactNode
  title: ReactNode
  facts?: ReactNode
  description?: ReactNode
  actions?: ReactNode
  /** `lg` is the record read view at 1:1: a 32px title with a 22px mono code. */
  size?: "md" | "lg"
  children?: ReactNode
  className?: string
}) {
  return (
    <header className={cx("flex flex-col gap-4", className)}>
      {((crumbs && crumbs.length > 0) || stamp) && (
        <div className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1">
          {crumbs && crumbs.length > 0 ? (
            <nav aria-label="Breadcrumb" className="crumb">
              {crumbs.map((crumb, i) => {
                const last = i === crumbs.length - 1
                return (
                  <span key={`${crumb.label}-${i}`}>
                    {i > 0 && " / "}
                    {crumb.href && !last ? (
                      <Link
                        href={crumb.href}
                        className="hover:text-primary lowercase"
                      >
                        {crumb.label}
                      </Link>
                    ) : (
                      <span
                        aria-current={last ? "page" : undefined}
                        className={last ? "text-primary" : "lowercase"}
                      >
                        {crumb.label}
                      </span>
                    )}
                  </span>
                )
              })}
            </nav>
          ) : (
            <span />
          )}
          {stamp && <span className="notation">{stamp}</span>}
        </div>
      )}

      <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div className="flex min-w-0 flex-col gap-1.5">
          <h1
            className={cx(
              "text-primary flex flex-wrap items-baseline gap-x-3.5 gap-y-1",
              size === "lg"
                ? "text-display-sm md:text-display-md"
                : "text-display-xs md:text-display-sm",
            )}
          >
            {code && (
              <>
                <span
                  className={cx(
                    "text-secondary font-mono leading-none font-semibold tracking-[-0.01em]",
                    size === "lg" ? "text-[22px]" : "text-[19px]",
                  )}
                >
                  {code}
                </span>
                <span
                  aria-hidden="true"
                  className={cx(
                    "bg-border-secondary hidden w-px self-center sm:block",
                    size === "lg" ? "h-6" : "h-5",
                  )}
                />
              </>
            )}
            <span className="min-w-0 flex-1 basis-64 text-pretty">{title}</span>
          </h1>
          {facts && <p className="notation">{facts}</p>}
          {description && (
            <p className="text-tertiary max-w-prose text-sm text-pretty">
              {description}
            </p>
          )}
          {children}
        </div>
        {actions && (
          <div className="flex flex-wrap items-center gap-2 md:shrink-0">
            {actions}
          </div>
        )}
      </div>
    </header>
  )
}
